import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ReactFlow, Background, Controls, Handle, Position, MarkerType } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "@dagrejs/dagre";
import { X } from "lucide-react";
import { HolderPick } from "@/components/HolderPick";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { fmtMoney, fmtPct, fmtPeriod, useApi } from "@/lib/api";
import { NoData } from "@/components/NoData";
import { PdfButton } from "@/components/ReportShell";

const KIND = {
  individual: "border-amber-500 bg-amber-50",
  trust: "border-slate-500 bg-slate-50",
  entity: "border-slate-900 bg-white",
};
const W = 210;
const H = 62;

const EntityNode = ({ data }) => (
  <div className={`border-2 px-3 py-2 w-[210px] ${KIND[data.kind] || KIND.entity} ${data.dim ? "opacity-25" : ""} ${data.selected ? "ring-2 ring-amber-500" : ""}`}
    data-testid={`org-node-${data.id}`}>
    <Handle type="target" position={Position.Left} className="!bg-slate-400" />
    <div className="text-[12px] font-medium text-slate-900 leading-tight truncate" title={data.name}>{data.name}</div>
    <div className="flex justify-between mt-1 text-[10px] font-mono text-slate-500">
      <span className="uppercase">{data.kind}</span><span>{fmtMoney(data.net_assets)}</span>
    </div>
    <Handle type="source" position={Position.Right} className="!bg-slate-400" />
  </div>
);
const nodeTypes = { entity: EntityNode };

function layout(nodes, edges) {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "LR", nodesep: 14, ranksep: 140 });
  g.setDefaultEdgeLabel(() => ({}));
  nodes.forEach((n) => g.setNode(n.id, { width: W, height: H }));
  edges.forEach((e) => g.setEdge(e.owner_id, e.owned_id));
  dagre.layout(g);
  return Object.fromEntries(nodes.map((n) => { const p = g.node(n.id); return [n.id, { x: p.x - W / 2, y: p.y - H / 2 }]; }));
}

function related(id, edges) {
  const keep = new Set([id]);
  const walk = (key, next) => {
    const stack = [id];
    while (stack.length) {
      const cur = stack.pop();
      edges.filter((e) => e[key] === cur).forEach((e) => { if (!keep.has(e[next])) { keep.add(e[next]); stack.push(e[next]); } });
    }
  };
  walk("owner_id", "owned_id");
  walk("owned_id", "owner_id");
  return keep;
}

const Panel = ({ node, edges, byId, onClose }) => {
  const owners = edges.filter((e) => e.owned_id === node.id);
  const holds = edges.filter((e) => e.owner_id === node.id);
  const Row = ({ e, other }) => (
    <li className="flex justify-between gap-2 py-1 border-b border-slate-100 text-xs">
      <span className="truncate">{byId[other]?.name}</span>
      <span className={`font-mono ${e.missing ? "text-red-600" : "text-slate-600"}`}>{e.missing ? "no %" : fmtPct(e.pct)}</span>
    </li>
  );
  return (
    <aside className="panel p-5 w-full lg:w-80 shrink-0 self-start" data-testid="org-detail-panel">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="small-label">{node.kind}{node.code ? ` · ${node.code}` : ""}</div>
          <div className="font-serif text-xl mt-1" data-testid="org-detail-name">{node.name}</div>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="org-detail-close"><X className="h-4 w-4" /></button>
      </div>
      <div className="font-mono text-lg mt-3">{fmtMoney(node.net_assets)}</div>
      <div className="flex flex-col gap-2 mt-4">
        <Link to={`/reports/entities/${node.id}`} className="text-sm underline" data-testid="org-open-valuation">Open entity valuation</Link>
        {node.kind !== "entity" && <Link to={`/reports/networth/${node.id}`} className="text-sm underline" data-testid="org-open-networth">Open net worth statement</Link>}
      </div>
      {owners.length > 0 && (<><div className="small-label mt-5">Owned by</div><ul className="mt-1">{owners.map((e) => <Row key={e.owner_id} e={e} other={e.owner_id} />)}</ul></>)}
      {holds.length > 0 && (<><div className="small-label mt-5">Holds</div><ul className="mt-1">{holds.map((e) => <Row key={e.owned_id} e={e} other={e.owned_id} />)}</ul></>)}
    </aside>
  );
};

export default function OwnershipChart() {
  const api = useApi();
  const { period } = useApp();
  const [g, setG] = useState(null);
  const [sel, setSel] = useState(null);
  const [focus, setFocus] = useState(true);

  useEffect(() => {
    if (period) api.get("/ownership/graph", { params: { period } }).then((r) => setG(r.data)).catch(() => setG({ nodes: [], edges: [], error: true }));
  }, [api, period]);

  const byId = useMemo(() => Object.fromEntries((g?.nodes || []).map((n) => [n.id, n])), [g]);
  const keep = useMemo(() => (sel && g ? related(sel, g.edges) : null), [sel, g]);
  const visible = useMemo(() => {
    if (!g) return { nodes: [], edges: [] };
    if (!keep || !focus) return g;
    return { nodes: g.nodes.filter((n) => keep.has(n.id)), edges: g.edges.filter((e) => keep.has(e.owner_id) && keep.has(e.owned_id)) };
  }, [g, keep, focus]);

  const flow = useMemo(() => {
    const pos = layout(visible.nodes, visible.edges);
    return {
      nodes: visible.nodes.map((n) => ({ id: n.id, type: "entity", position: pos[n.id], data: { ...n, selected: n.id === sel, dim: keep && !focus && !keep.has(n.id) } })),
      edges: visible.edges.map((e) => ({
        id: `${e.owner_id}-${e.owned_id}`, source: e.owner_id, target: e.owned_id,
        label: e.missing ? "no %" : fmtPct(e.pct),
        labelStyle: { fontSize: 10, fontFamily: "monospace", fill: e.missing ? "#DC2626" : "#234E70" },
        style: { stroke: e.missing ? "#FCA5A5" : "#7FA7BF", strokeDasharray: e.missing ? "4 3" : undefined },
        markerEnd: { type: MarkerType.ArrowClosed, color: e.missing ? "#FCA5A5" : "#7FA7BF" },
      })),
    };
  }, [visible, sel, keep, focus]);

  const onNodeClick = useCallback((_, n) => setSel(n.id), []);

  if (!period) return <NoData />;
  return (
    <div data-testid="ownership-chart-page">
      <div className="small-label">Month end {fmtPeriod(period)}</div>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">Ownership Chart</h1>
      <p className="text-slate-600 mt-2 text-sm max-w-2xl">Arrows point from owner to the entity it owns. Click any box to see its owners and holdings and open its statement. Dashed red links are holdings with no percentage yet.</p>
      <div className="flex flex-wrap items-center gap-3 mt-6">
        <div className="w-full sm:w-80">
          <HolderPick nodes={g?.nodes || []} value={sel} onChange={setSel} />
        </div>
        {sel && (
          <>
            <Button variant={focus ? "default" : "outline"} className="rounded-sm" onClick={() => setFocus(!focus)} data-testid="org-focus-toggle">{focus ? "Showing connections only" : "Showing everything"}</Button>
            <Button variant="ghost" className="rounded-sm" onClick={() => setSel(null)} data-testid="org-clear-selection">Show full chart</Button>
            <PdfButton api={api} url={`/ownership/graph/${sel}/pdf?period=${period}`} name={`OwnershipChart_${(byId[sel]?.name || "holder").replace(/[^A-Za-z0-9]+/g, "_")}_${period}.pdf`} label="Download chart" testid="org-download-chart" variant="outline" />
          </>
        )}
        <div className="flex gap-3 text-xs text-slate-500 sm:ml-auto">
          <span className="flex items-center gap-1"><span className="h-3 w-3 border-2 border-slate-900" />Entity</span>
          <span className="flex items-center gap-1"><span className="h-3 w-3 border-2 border-slate-500 bg-slate-50" />Trust</span>
          <span className="flex items-center gap-1"><span className="h-3 w-3 border-2 border-amber-500 bg-amber-50" />Individual</span>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row gap-4 mt-4">
        <div className="panel w-full lg:flex-1 h-[70vh] min-w-0" data-testid="org-canvas">
          {g?.error && <p className="p-6 text-sm text-red-700" data-testid="org-load-error">Couldn't load the chart. Please refresh.</p>}
          {g && !g.error && (
            <ReactFlow key={`${sel}-${focus}`} nodes={flow.nodes} edges={flow.edges} nodeTypes={nodeTypes} onNodeClick={onNodeClick}
              fitView fitViewOptions={{ minZoom: 0.35 }} minZoom={0.05} nodesDraggable={false} nodesConnectable={false} proOptions={{ hideAttribution: true }}>
              <Background color="#DEDAD0" gap={24} />
              <Controls showInteractive={false} />
            </ReactFlow>
          )}
        </div>
        {sel && byId[sel] && <Panel node={byId[sel]} edges={g.edges} byId={byId} onClose={() => setSel(null)} />}
      </div>
    </div>
  );
}
